import TableRow from '@mui/material/TableRow'
import TableCell from '@mui/material/TableCell'
import Typography from '@mui/material/Typography'
import Collapse from '@mui/material/Collapse'
import Box from '@mui/material/Box'
import Table from '@mui/material/Table'
import TableHead from '@mui/material/TableHead'
import TableBody from '@mui/material/TableBody'
import IconButton from '@mui/material/IconButton'
import Chip from '@mui/material/Chip'
import Stack from '@mui/material/Stack'
import Avatar from '@mui/material/Avatar'

// hooks
// types
// components
import { masks } from 'src/utils/masks'
import { format } from 'date-fns'
import { useState } from 'react'
import Iconify from 'src/components/iconify'
//

// ----------------------------------------------------------------------

type Props = {
  selected: boolean
  onEditRow: VoidFunction
  row: any
  refetch: VoidFunction
  onSelectRow: VoidFunction
  onDeleteRow: VoidFunction
}

const paymentTypes: Record<string, { label: string; color: any; icon: string }> = {
  money: { label: 'Dinheiro', color: 'success', icon: 'solar:money-bold' },
  credit: { label: 'Crédito', color: 'error', icon: 'solar:card-bold' },
  debit: { label: 'Débito', color: 'info', icon: 'solar:card-2-bold' },
  pix: { label: 'PIX', color: 'warning', icon: 'solar:smartphone-bold' },
  card: { label: 'CashLess', color: 'secondary', icon: 'solar:card-send-bold' },
}

export default function OrderTableRow({
  row,
  selected,
  refetch,
  onEditRow,
  onSelectRow,
  onDeleteRow,
}: Props) {
  const {
    event,
    products,
    createdAt,
    saler,
    paymentType,
    tax,
    total,
    number,
    terminalSerial,
  } = row

  const [open, setOpen] = useState(false)

  const paymentConfig = paymentTypes[paymentType] || paymentTypes.money

  return (
    <>
      <TableRow hover selected={selected} sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
        <TableCell>
          <Stack direction='row' alignItems='center' spacing={1.5}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                px: 1,
                py: 0.5,
                borderRadius: 1,
                bgcolor: 'primary.lighter',
                border: 1,
                borderColor: 'primary.light',
                minWidth: 'fit-content',
              }}
            >
              <Iconify icon='solar:hashtag-bold' width={14} color='primary.main' />
              <Typography
                variant='caption'
                fontWeight={600}
                color='primary.main'
                sx={{ whiteSpace: 'nowrap' }}
              >
                {number || '?'}
              </Typography>
            </Box>
            <IconButton
              size='small'
              onClick={() => setOpen(!open)}
              sx={{
                color: open ? 'primary.main' : 'text.secondary',
                '&:hover': { bgcolor: 'primary.lighter' },
              }}
            >
              <Iconify
                icon={open ? 'solar:alt-arrow-up-bold' : 'solar:alt-arrow-down-bold'}
                width={16}
              />
            </IconButton>
          </Stack>
        </TableCell>

        <TableCell>
          <Box>
            <Typography variant='body2' fontWeight={500} color='text.primary'>
              {event?.name || 'N/A'}
            </Typography>
            <Typography variant='caption' color='text.secondary'>
              {format(new Date(createdAt), 'dd/MM/yyyy HH:mm:ss')}
            </Typography>
          </Box>
        </TableCell>

        <TableCell>
          <Stack direction='row' alignItems='center' spacing={1}>
            <Avatar sx={{ width: 24, height: 24, fontSize: 12 }}>
              {saler?.name?.charAt(0).toUpperCase() || 'U'}
            </Avatar>
            <Typography variant='body2' fontWeight={500}>
              {saler?.name || 'N/A'}
            </Typography>
          </Stack>
        </TableCell>

        <TableCell>
          <Chip
            size='small'
            icon={<Iconify icon={paymentConfig.icon} width={16} />}
            label={paymentConfig.label}
            color={paymentConfig.color}
            variant='soft'
          />
        </TableCell>

        <TableCell>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              px: 1,
              py: 0.5,
              borderRadius: 1,
              bgcolor: 'secondary.lighter',
              border: 1,
              borderColor: 'secondary.light',
              width: 'fit-content',
            }}
          >
            <Iconify icon='solar:monitor-bold' width={14} color='secondary.main' />
            <Typography variant='caption' fontWeight={600} color='secondary.main'>
              {terminalSerial || 'N/A'}
            </Typography>
          </Box>
        </TableCell>

        <TableCell>
          <Typography variant='body2' fontWeight={600} color='success.main'>
            {masks.currencyAllPlatforms(total)}
          </Typography>
        </TableCell>

        <TableCell>
          <Typography variant='body2' color='warning.main'>
            {masks.currencyAllPlatforms(tax)}
          </Typography>
        </TableCell>

        <TableCell>
          <Typography variant='body2' fontWeight={600} color='info.main'>
            {masks.currencyAllPlatforms(total - tax)}
          </Typography>
        </TableCell>

        <TableCell>
          <Typography variant='caption' color='text.secondary'>
            {format(new Date(createdAt), 'HH:mm:ss')}
          </Typography>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={10}>
          <Collapse in={open} timeout='auto' unmountOnExit>
            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, m: 1 }}>
              <Stack direction='row' alignItems='center' spacing={1} mb={2}>
                <Iconify icon='solar:bag-2-bold' width={20} color='primary.main' />
                <Typography variant='subtitle2' color='primary.main'>
                  Produtos do Pedido
                </Typography>
                <Chip size='small' label={`${products?.length || 0} itens`} />
              </Stack>

              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Produto</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Preço Unit.</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align='center'>
                      Qtd.
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align='right'>
                      Total
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {products?.map((product: any, index: number) => (
                    <TableRow
                      key={product._id || index}
                      sx={{ '&:hover': { bgcolor: 'action.hover' } }}
                    >
                      <TableCell>
                        <Stack direction='row' alignItems='center' spacing={2}>
                          <Avatar
                            sx={{
                              width: 32,
                              height: 32,
                              bgcolor: 'success.lighter',
                              color: 'success.main',
                              fontSize: 12,
                            }}
                          >
                            {product.product?.name?.charAt(0).toUpperCase() || 'P'}
                          </Avatar>
                          <Box>
                            <Typography variant='body2' fontWeight={500}>
                              {product.product?.name || product.name || 'Produto sem nome'}
                            </Typography>
                            <Typography variant='caption' color='text.secondary'>
                              Item #{product.number || index + 1}
                            </Typography>
                          </Box>
                        </Stack>
                      </TableCell>

                      <TableCell>
                        <Typography variant='body2' color='text.primary'>
                          {masks.currencyAllPlatforms(product?.price)}
                        </Typography>
                      </TableCell>

                      <TableCell align='center'>
                        <Chip
                          size='small'
                          label={`${product.quantity}x`}
                          color='info'
                          variant='soft'
                        />
                      </TableCell>

                      <TableCell align='right'>
                        <Typography variant='body2' fontWeight={600} color='success.main'>
                          {masks.currencyAllPlatforms(
                            (product?.price || 0) * (product?.quantity || 1),
                          )}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  )
}
